import {PHASES, WAR_PHASE_STAGES} from "../../../constants/phases";
import {GAME_TYPES} from "../../../constants/game";
import {
  agrarianismCommitConversion,
  applyManeuverActions,
  applyMeleeDamage,
  applyMovementActions,
  applyRecruitmentActions,
  applyShootingActions,
  calculateMeleeCombatDamage,
  captureCities,
  completePhase,
  completePlayerSupply,
  setInvalidPendingBuildings,
  setWarPhaseStage,
  showToast, withToastIcon,
  startNextPhase
} from "../../../redux/actions";
import {
  selectCurrentPlayer,
  selectGameType,
  selectHexes,
  selectPhase,
  selectPlayers,
  selectSelectedFarmsForConversion,
  selectSharedGameState,
  selectUnits,
  selectWarPhaseStage
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {getTimestampFromStore} from "../../other/getTimestampFromStore";
import {getPlayerUnits} from "../../players/getPlayerUnits";
import {hasAnyUnitWithAdjacentEnemy} from "../hasAdjacentEnemyUnit";
import {calcMeleeCombatInput} from "../melee/calcMeleeCombatInput";
import {checkIfUnitCouldManeuverFromHex} from "../maneuver/checkIfUnitCouldManeuverFromHex";
import {
  buildInvalidPendingBuildingsMessage,
  INVALID_PENDING_BUILDINGS_HEADERS,
  validateCurrentPlayerPendingBuildings
} from "../construction/pendingBuildingsValidation";
import {rollbackInvalidPendingBuildings} from "../construction/rollbackInvalidPendingBuildings";
import {getRandomPolicyForMarket} from "../../policy/getRandomPolicyForMarket";
import {createPhaseActionGuard} from "./phaseActionGuard";
import {checkIfTurnInputBlocked, notifyTurnInputBlocked} from "./turnInputLock";
import {isPlayerPolicy} from "../../factions/isPlayerPolicy";
import {POLICIES} from "../../../constants/policies";
import {getCoinsActionText} from "../text/getCoinsText";
import {calcAgrarianismConversion} from "../../policy/agrarianism/calcAgrarianismConversion";
import {checkIfParallelSupplyPhase, checkIfPlayerCompletedPhase} from "../supply/parallelSupply";
import {getActingPlayerFromState} from "../../players/getActingPlayer";
import {checkIfUnitCanShoot} from "../progress/checkIfUnitCanShoot";
import {checkIfUnitCanUseAbility} from "../progress/checkIfUnitCanUseAbility";
import {TOAST_ICONS} from "../../../constants/toast";

/**
 * Игрок playerId заканчивает свою часть одновременной фазы снабжения сетевой
 * игры (см. utils/gameState/supply/parallelSupply): обмен выбранного по
 * аграризму снабжения на монеты (как при обычном завершении хода) и
 * COMPLETE_PLAYER_SUPPLY. Итог снабжения не применяется и фаза не
 * закрывается — это делает сервер, когда закончили все.
 *
 * Один и тот же путь у кнопки завершения хода, модалок подтверждения,
 * авто-действий, ботов, серверного таймера и MCP.
 *
 * @returns {boolean} true — завершение отправлено
 */
export const completeParallelSupplyForPlayer = (store, playerId, {isClientExecutor = false, playSound = false, soundManager = null} = {}) => {
  const state = store.getState();
  const sharedGameState = selectSharedGameState(state);
  if (!checkIfParallelSupplyPhase(sharedGameState) || checkIfPlayerCompletedPhase(sharedGameState, playerId)) {
    return false;
  }
  const player = selectPlayers(state).find((candidate) => candidate.id === playerId);
  if (!player) {
    return false;
  }

  const selectedFarmsForConversion = selectSelectedFarmsForConversion(state, playerId);
  if (isPlayerPolicy(player, POLICIES.AGRARIANISM) && selectedFarmsForConversion.length >= 2) {
    if (isClientExecutor) {
      const {coins, usedSupplyCount} = calcAgrarianismConversion(selectedFarmsForConversion);
      const coinsText = getCoinsActionText(coins);
      store.dispatch(withToastIcon(showToast(`Вы обменяли ${usedSupplyCount} снабжения на ${coins} ${coinsText}.`, 'success', 5000, true, true), TOAST_ICONS.COINS));
      if (playSound && soundManager) {
        soundManager.playCoins(coins);
      }
    }
    store.dispatch(agrarianismCommitConversion(selectedFarmsForConversion, playerId));
  }

  // Слепок — после обмена: обмен фазу не меняет, а текущий игрок в
  // одновременной фазе стоит на месте
  store.dispatch(completePlayerSupply(playerId, createPhaseActionGuard(selectSharedGameState(store.getState()))));

  if (isClientExecutor && playSound && soundManager) {
    soundManager.playTurnEnd();
  }
  return true;
};


export const handleCompleteTurn = (store, isClientExecutor, playSound, soundManager, {playerId} = {}) => {
  const state = store.getState();
  const phase = selectPhase(state);

  // Время хода игрока вышло, сервер вот-вот закроет ход сам (см. turnInputLock):
  // завершение не выполняется целиком — без звуков и подсказок об обмене и
  // без экшенов, которые страж блокировки все равно отбросил бы. Сюда приходят
  // и подтверждения из модалок («остались фермы» и т.п.), открытых до нуля
  if (isClientExecutor && checkIfTurnInputBlocked(state)) {
    notifyTurnInputBlocked(store.dispatch, state);
    return;
  }

  // Одновременная фаза снабжения: «завершить ход» — закончить СВОЮ часть фазы.
  // Чью — явный playerId, а на клиенте без него — действующий игрок
  // пользователя. Серверу без playerId завершать некого: текущий игрок в этой
  // фазе только якорь слепка хода, а не ходящий
  if (checkIfParallelSupplyPhase(selectSharedGameState(state))) {
    const actorId = playerId || (isClientExecutor ? getActingPlayerFromState(state)?.id : null);
    if (!actorId) {
      console.warn('[handleCompleteTurn] parallel supply completion without player');
      return;
    }
    completeParallelSupplyForPlayer(store, actorId, {isClientExecutor, playSound, soundManager});
    return;
  }
  const currentPlayer = selectCurrentPlayer(state);
  const selectedFarmsForConversion = selectSelectedFarmsForConversion(state);

  if (phase === PHASES.SUPPLY) {
    if (isPlayerPolicy(currentPlayer, POLICIES.AGRARIANISM) && selectedFarmsForConversion.length >= 2) {
      if (isClientExecutor) {
        const {coins, usedSupplyCount} = calcAgrarianismConversion(selectedFarmsForConversion);
        let coinsText = getCoinsActionText(coins);
        store.dispatch(withToastIcon(showToast(`Вы обменяли ${usedSupplyCount} снабжения на ${coins} ${coinsText}.`, 'success', 5000, true, true), TOAST_ICONS.COINS));
        if (playSound && soundManager) {
          soundManager.playCoins(coins);
        }
      }
      store.dispatch(agrarianismCommitConversion(selectedFarmsForConversion));
    }
  }

  // В фазе войны при завершении этапа найма переходим к этапу перемещения
  if (phase === PHASES.WAR) {
    executeWarPhaseStageCompletion(store, isClientExecutor, playSound, soundManager);
    return;
  }

  // В фазе строительства проверяем pending commonBuildings перед завершением
  if (phase === PHASES.CONSTRUCTION) {
    if (isClientExecutor) {
      // Та же проверка и тот же текст, что при применении плана строительства в
      // начале хода (см. pendingBuildingsValidation)
      const validationResult = validateCurrentPlayerPendingBuildings(state);

      if (!validationResult.isValid) {
        // Есть невалидные строения - показываем ошибку и сохраняем их для отображения красных молотков
        const invalidHexKeys = validationResult.invalidBuildings.map(b => b.hexKey);
        store.dispatch(setInvalidPendingBuildings(invalidHexKeys));
        store.dispatch(showToast(
          buildInvalidPendingBuildingsMessage(validationResult, INVALID_PENDING_BUILDINGS_HEADERS.CLIENT), 'warning', 5000
        ));
        return;
      }
    } else {
      // Ход закрывает не сам игрок, исправить размещение некому: некорректные
      // строения и то, что без них нельзя было построить, снимаются, остальные
      // действия хода остаются
      rollbackInvalidPendingBuildings(store);
    }
  }

  // Для других фаз - стандартное поведение
  executePhaseCompletion(store, isClientExecutor, playSound, soundManager);
};

const getTimestampForTurnEndAction = (store, isClientExecutor) => {
  // В онлайн-игре клиент не должен передавать timestamp — его выставляет сервер отдельным экшеном.
  // В локальной игре и при серверном таймауте timestamp по-прежнему передаётся через исходные экшены.
  if (isClientExecutor && selectGameType(store.getState()) !== GAME_TYPES.LOCAL) {
    return null;
  }
  return getTimestampFromStore(store);
};

export const executePhaseCompletion = (store, isClientExecutor, playSound, soundManager) => {
  const state = store.getState();
  const sharedGameState = selectSharedGameState(state);
  const phase = selectPhase(state);
  const players = selectPlayers(state);
  const guard = createPhaseActionGuard(sharedGameState);

  // Завершенность фазы считается МНОЖЕСТВОМ, а не length + 1: текущий игрок
  // может уже находиться в phaseCompletedPlayers — например, парный
  // startNextPhase предыдущего завершения потерялся на обрыве сокета делегата.
  // Тогда COMPLETE_PHASE схлопывается в редьюсере по isAlreadyCompleted, а
  // length + 1 давал бы N+1 !== N, startNextPhase не диспатчился бы никогда —
  // и матч зависал НАВСЕГДА на ходе этого игрока (серверный таймер и делегат
  // бесконечно бились бы об одно и то же состояние; пойман на проде, раунд 10,
  // ход бота). Множество в этом состоянии дает «все завершили» → startNextPhase
  // уходит со свежим слепком и матч самоизлечивается ближайшим же завершением
  const completedWithCurrent = new Set(sharedGameState.phaseCompletedPlayers);
  const currentPlayer = players[sharedGameState.currentPlayerIndex];
  if (currentPlayer) {
    completedWithCurrent.add(currentPlayer.id);
  }
  const allPlayersCompleted = completedWithCurrent.size === players.length;

  const timestamp = getTimestampForTurnEndAction(store, isClientExecutor);

  store.dispatch(completePhase(timestamp, guard));

  if (allPlayersCompleted && phase === PHASES.WAR) {
    // Обрабатываем захват городов перед переходом к следующей фазе.
    // Гвард ограничен round/phase (без currentPlayerId/warPhaseStage) —
    // COMPLETE_PHASE чуть выше уже мог сдвинуть текущего игрока в рамках
    // этого же завершения хода, а раунд/фаза на этом шаге еще не менялись.
    // Время захвата — то же, что у завершения фазы (в сетевой партии клиент
    // его не знает — ставит сервер)
    store.dispatch(captureCities({round: guard.round, phase: guard.phase}, timestamp));
  }

  // Если все игроки завершили текущую фазу
  if (allPlayersCompleted) {
    store.dispatch(startNextPhase(timestamp, getRandomPolicyForMarket(), guard));
  }

  // Звук — строго ПОСЛЕ обоих диспатчей: completePhase и startNextPhase —
  // неразрывная пара, и исключение из звукового слоя между ними оставило бы
  // матч в абсорбирующем состоянии «все завершили, а переход фазы не ушел»
  // (один из путей прод-зависания на ходе бота; исполнитель ходов бота
  // проглатывает исключения и продолжает тикать)
  if (isClientExecutor && playSound && soundManager) {
    soundManager.playTurnEnd();
  }
}

const proceedToWarStage = (stage, store, isClientExecutor, playSound, soundManager) => {
  const timestamp = getTimestampForTurnEndAction(store, isClientExecutor);
  const guard = createPhaseActionGuard(selectSharedGameState(store.getState()));
  store.dispatch(setWarPhaseStage(stage, timestamp, guard));
  if (isClientExecutor && playSound && soundManager) {
    soundManager.playWarStageEnd();
  }
}

// Функция для завершения только текущего этапа фазы войны (без завершения всей фазы)
export const executeWarPhaseStageCompletion = (store, isClientExecutor, playSound, soundManager) => {
  const state = store.getState();
  const warPhaseStage = selectWarPhaseStage(state);
  const currentPlayer = selectCurrentPlayer(state);

  const getUpdatedUnits = () => {
    const updatedState = store.getState();
    return selectUnits(updatedState);
  }

  const getUpdatedPlayerUnits = () => {
    const updatedUnits = getUpdatedUnits();
    return getPlayerUnits(currentPlayer, updatedUnits);
  }

  // Есть ли у игрока отряд, способный стрелять прямо сейчас — для переходов
  // MOVEMENT → SHOOTING/ABILITIES и ABILITIES → SHOOTING. Тот же предикат
  // считает дела этапа стрельбы на кнопке хода (progress/)
  const checkHasShootingCapableUnit = (updatedPlayerUnits) => {
    const updatedState = store.getState();
    return updatedPlayerUnits.some(unit => checkIfUnitCanShoot(updatedState, unit, currentPlayer));
  }

  // Есть ли у игрока отряд, способный применить способность этапа ABILITIES
  // (Суккубы, Гоблины) — для перехода MOVEMENT → ABILITIES. Тот же предикат
  // считает дела этапа способностей на кнопке хода (progress/)
  const checkHasAbilityCapableUnit = (updatedPlayerUnits) => {
    const updatedState = store.getState();
    return updatedPlayerUnits.some(unit => checkIfUnitCanUseAbility(updatedState, unit, currentPlayer));
  }

  const switchToCombatStage = () => {
    const updatedUnits = getUpdatedUnits();
    const hasUnitWithAdjacentEnemy = hasAnyUnitWithAdjacentEnemy(currentPlayer.id, updatedUnits);

    if (!hasUnitWithAdjacentEnemy) {
      // Не executePhaseCompletion() напрямую - иначе отряд, применивший
      // способность (Суккубы/Гоблины) и оставшийся без смежного врага для
      // ближнего боя, никогда не проверялся бы на право манёвра (найдено
      // пользователем: этап манёвра пропускался целиком, даже когда манёвр
      // должен был быть доступен только за счёт способности, см.
      // add-goblins-faction design.md)
      switchToManeuverStageOrCompletePhase();
    } else {
      // Переходим к этапу ближнего боя фазы войны текущего игрока
      proceedToWarStage(WAR_PHASE_STAGES.COMBAT, store, isClientExecutor, playSound, soundManager);

      // Рассчитываем урон ближнего боя (players — для подкрутки d6 ботов
      // уровнем бонуса, см. getBotD6MinRoll)
      const meleeCombatInput = calcMeleeCombatInput(
        updatedUnits, currentPlayer.id, [], {}, selectPlayers(store.getState())
      );
      store.dispatch(calculateMeleeCombatDamage(meleeCombatInput));

      if (isClientExecutor && playSound && soundManager) {
        soundManager.playMeleeCombat();
      }
    }
  }

  const switchToManeuverStageOrCompletePhase = () => {
    const updatedState = store.getState();
    const updatedHexes = selectHexes(updatedState);
    const updatedPlayerUnits = getUpdatedPlayerUnits();
    const hasUnitThatCouldManeuver = updatedPlayerUnits.some(unit => {
      return checkIfUnitCouldManeuverFromHex(currentPlayer, unit, updatedHexes)
    });

    if (hasUnitThatCouldManeuver) {
      proceedToWarStage(WAR_PHASE_STAGES.MANEUVER, store, isClientExecutor, playSound, soundManager);
    } else {
      executePhaseCompletion(store, isClientExecutor, playSound, soundManager);
    }
  }

  if (warPhaseStage === WAR_PHASE_STAGES.RECRUITMENT) {
    store.dispatch(applyRecruitmentActions());

    const updatedPlayerUnits = getUpdatedPlayerUnits();

    if (updatedPlayerUnits.length === 0) {
      executePhaseCompletion(store, isClientExecutor, playSound, soundManager);
      return;
    }

    proceedToWarStage(WAR_PHASE_STAGES.MOVEMENT, store, isClientExecutor, playSound, soundManager);
    return;
  }

  if (warPhaseStage === WAR_PHASE_STAGES.MOVEMENT) {
    store.dispatch(applyMovementActions());

    const updatedUnits = getUpdatedUnits();
    const updatedPlayerUnits = getPlayerUnits(currentPlayer, updatedUnits);

    const hasShootingCapableUnit = checkHasShootingCapableUnit(updatedPlayerUnits);

    // Суккубы: этап способностей идет раньше стрельбы (см. design.md Decision 1)
    const hasAbilityCapableUnit = checkHasAbilityCapableUnit(updatedPlayerUnits);

    if (hasAbilityCapableUnit) {
      proceedToWarStage(WAR_PHASE_STAGES.ABILITIES, store, isClientExecutor, playSound, soundManager);
    } else if (hasShootingCapableUnit) {
      proceedToWarStage(WAR_PHASE_STAGES.SHOOTING, store, isClientExecutor, playSound, soundManager);
    } else {
      const hasUnitWithAdjacentEnemy = hasAnyUnitWithAdjacentEnemy(currentPlayer.id, updatedUnits);

      if (hasUnitWithAdjacentEnemy) {
        switchToCombatStage();
      } else {
        // На этом шаге ни один отряд ещё не мог использовать способность
        // (иначе выше сработал бы hasAbilityCapableUnit) - манёвр здесь
        // заведомо недоступен, но проверяем через ту же функцию, что и везде
        // остальные выходы из боевых этапов, а не executePhaseCompletion()
        // напрямую - ради единообразия и на случай будущих условий манёвра
        switchToManeuverStageOrCompletePhase();
      }
    }
    return;
  }

  if (warPhaseStage === WAR_PHASE_STAGES.ABILITIES) {
    // Эффект соблазнения уже применен немедленно при попытке (см. design.md
    // Decision 2) — здесь, в отличие от MOVEMENT/SHOOTING, нет своего applyXActions()
    const updatedUnits = getUpdatedUnits();
    const updatedPlayerUnits = getPlayerUnits(currentPlayer, updatedUnits);

    const hasShootingCapableUnit = checkHasShootingCapableUnit(updatedPlayerUnits);

    if (hasShootingCapableUnit) {
      proceedToWarStage(WAR_PHASE_STAGES.SHOOTING, store, isClientExecutor, playSound, soundManager);
    } else {
      const hasUnitWithAdjacentEnemy = hasAnyUnitWithAdjacentEnemy(currentPlayer.id, updatedUnits);

      if (hasUnitWithAdjacentEnemy) {
        switchToCombatStage();
      } else {
        // Не executePhaseCompletion() напрямую - отряд мог уже применить
        // способность на этом же этапе (Суккубы/Гоблины) и остаться без
        // смежного врага для ближнего боя и без способности стрелять - без
        // этой проверки этап манёвра пропускался бы целиком, даже когда
        // манёвр положен за способность (найдено пользователем в ручном
        // тестировании, см. add-goblins-faction design.md)
        switchToManeuverStageOrCompletePhase();
      }
    }
    return;
  }

  if (warPhaseStage === WAR_PHASE_STAGES.SHOOTING) {
    store.dispatch(applyShootingActions());

    const updatedUnits = getUpdatedUnits();
    const hasUnitWithAdjacentEnemy = hasAnyUnitWithAdjacentEnemy(currentPlayer.id, updatedUnits);

    if (hasUnitWithAdjacentEnemy) {
      switchToCombatStage();
    } else {
      // Раньше здесь была отдельная ветка на pendingShootingActions.length —
      // костыль именно под стрельбу: манёвр проверялся, только если отряд
      // реально стрелял, а без выстрела сразу шло executePhaseCompletion(),
      // минуя манёвр за способность (Суккубы/Гоблины), применённую раньше на
      // этапе ABILITIES. switchToManeuverStageOrCompletePhase() сам корректно
      // ничего не находит и завершает фазу, если манёвр действительно
      // недоступен ни по одной причине - отдельное условие было лишним
      switchToManeuverStageOrCompletePhase();
    }
    return;
  }

  if (warPhaseStage === WAR_PHASE_STAGES.COMBAT) {
    // Применяем урон ближнего боя. Слепок — из состояния ДО применения: этап
    // может закрыть и сервер (одновременные последние подтверждения), и тогда
    // поздний дубль по слепку схлопнется no-op'ом
    store.dispatch(applyMeleeDamage(createPhaseActionGuard(selectSharedGameState(store.getState()))));

    switchToManeuverStageOrCompletePhase();
  }

  if (warPhaseStage === WAR_PHASE_STAGES.MANEUVER) {
    store.dispatch(applyManeuverActions());
    executePhaseCompletion(store, isClientExecutor, playSound, soundManager);
  }
}
